import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  Chip,
  Pagination,
  Spinner,
  Tab,
  Tabs,
  Modal,
  ModalContent,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { Link } from 'wouter';
import { toast } from 'react-toastify';
import {
  InboxClaim,
  InboxFile,
  InboxList,
  claimInboxFile,
  inboxRequest,
} from 'common/src/api/settlement-inbox';
import { useTranslation } from 'common/src/hooks/useTranslation';
import useUserStore from 'common/src/stores/useUserStore';
import Header from '../../../components/layouts/Header';
import {
  Alert,
  EmptyState,
  LoadingState,
  PageLayout,
  StatusChip,
} from '../../../components/ui';
import ExcelUploadModal from '../../../components/settlements/excel-upload-modal';
import MultiShipmentUploadModal from '../../../components/settlements/multi-shipment-upload-modal';
import SettlementSourcePreview, {
  RemoteSettlementDocument,
} from '../../../components/settlements/settlement-source-preview';
import InboxSenderApprovals from '../../../components/settlements/inbox-sender-approvals';
import useSettlementInboxAccess from '../../../hooks/useSettlementInboxAccess';
import AccessDeniedPage from '../../access-denied';

const statuses = {
  quarantined: 'Verificando archivo',
  ready: 'No procesado',
  reviewing: 'En revisión',
  saving: 'Guardando',
  completed: 'Procesado',
  duplicate: 'Duplicado',
  rejected: 'No compatible',
};
const reasons: Record<string, string> = {
  SCAN_BLOCKED:
    'Archivo bloqueado por seguridad. Solicita al remitente una copia segura, sin cifrado ni macros.',
  SCAN_UNAVAILABLE:
    'El archivo sigue en cuarentena. Los errores temporales se reintentan automáticamente hasta 5 veces.',
  FILE_TOO_LARGE: 'El archivo supera el límite: Excel 10 MB, PDF 20 MB.',
  UNSUPPORTED_FILE: 'Solo se admiten archivos PDF, XLS y XLSX.',
  INVALID_FILE:
    'El contenido del archivo no coincide con un documento admitido.',
};

export default function SettlementInbox() {
  const access = useSettlementInboxAccess();
  const accountKey = useUserStore(
    (s) => `${s.userProfile?.auth_id}:${s.userProfile?.exporter_id}`
  );
  const { t } = useTranslation('inbox');
  if (access.loading) return <LoadingState />;
  if (access.failed && !access.enabled)
    return (
      <PageLayout>
        <Header
          title={t('Bandeja de entrada')}
          description={t(
            'Recibe liquidaciones por correo y procésalas desde aquí.'
          )}
        />
        <EmptyState
          icon='tabler:alert-circle'
          title={t('No se pudo cargar la bandeja. Vuelve a intentar.')}
          action={
            <Button variant='flat' onPress={() => void access.refresh()}>
              {t('Reintentar')}
            </Button>
          }
        />
      </PageLayout>
    );
  if (!access.enabled) return <AccessDeniedPage />;
  return (
    <SettlementInboxContent key={accountKey} accessFailed={access.failed} />
  );
}

function SettlementInboxContent({ accessFailed }: { accessFailed: boolean }) {
  const { t, currentLanguage } = useTranslation('inbox');
  const exporterId = useUserStore((s) => s.userProfile?.exporter_id);
  const isSuperadmin = useUserStore((s) =>
    s.userProfile?.roles.includes('superadmin')
  );
  const [data, setData] = useState<InboxList | null>(null);
  // Address configuration is independent of file filters/pagination. The keyed
  // parent resets it when changing accounts/exporters, never during a list reload.
  const [addresses, setAddresses] = useState<InboxList['addresses']>([]);
  const [filter, setFilter] = useState('pending');
  const [section, setSection] = useState('files');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [claim, setClaim] = useState<InboxClaim | null>(null);
  const [processTarget, setProcessTarget] = useState<InboxFile | null>(null);
  const [processMode, setProcessMode] = useState<'single' | 'multi' | null>(
    null
  );
  const claimRef = useRef<InboxClaim | null>(null);
  const requestNumber = useRef(0);
  const [preview, setPreview] = useState<
    (RemoteSettlementDocument & { settlementIds: number[] }) | null
  >(null);

  const load = useCallback(
    async (silent = false) => {
      const sequence = ++requestNumber.current;
      if (!silent) setLoading(true);
      try {
        const result = await inboxRequest<InboxList>('list', {
          filter,
          page: page - 1,
        });
        if (sequence === requestNumber.current) {
          setData(result);
          setAddresses(result.addresses);
          setError(false);
        }
      } catch {
        if (sequence === requestNumber.current) setError(true);
      } finally {
        if (sequence === requestNumber.current) setLoading(false);
      }
    },
    [filter, page, exporterId]
  );

  useEffect(() => {
    setData(null);
    void load();
    const timer = window.setInterval(() => {
      if (!document.hidden) void load(true);
    }, 30_000);
    return () => {
      clearInterval(timer);
      requestNumber.current++;
    };
  }, [load]);

  const closeClaim = useCallback(() => {
    const current = claimRef.current;
    claimRef.current = null;
    setClaim(null);
    setProcessMode(null);
    if (current)
      void inboxRequest('release', { id: current.id, token: current.token })
        .finally(() => load(true))
        .catch(() => undefined);
  }, [load]);

  useEffect(() => {
    // Exporter switches/unmounts clear file data and release the processing lease.
    setPreview(null);
    setClaim(null);
    setProcessTarget(null);
    setProcessMode(null);
    return () => {
      const current = claimRef.current;
      claimRef.current = null;
      if (current)
        void inboxRequest('release', {
          id: current.id,
          token: current.token,
        }).catch(() => undefined);
    };
  }, [exporterId]);

  useEffect(() => {
    if (!claim) return;
    const timer = window.setInterval(() => {
      void inboxRequest('renew', { id: claim.id, token: claim.token }).catch(
        () => {
          toast.error(
            t('La sesión de revisión venció. Abre el archivo nuevamente.')
          );
          closeClaim();
        }
      );
    }, 5 * 60_000);
    return () => clearInterval(timer);
  }, [claim, closeClaim, t]);

  const act = async (id: string, task: () => Promise<void>) => {
    setBusy(id);
    try {
      await task();
    } catch (cause) {
      toast.error(
        t(
          cause instanceof Error && /BUSY|LEASE/.test(cause.message)
            ? 'Otro usuario está revisando este archivo o la sesión venció.'
            : 'No se pudo completar la acción. Intenta nuevamente.'
        )
      );
    } finally {
      setBusy(null);
    }
  };
  const copy = (text: string) =>
    act('copy', async () => {
      await navigator.clipboard.writeText(text);
      toast.success(t('Copiado'));
    });
  const process = (file: InboxFile, mode: 'single' | 'multi') =>
    act(file.id, async () => {
      const next = await claimInboxFile(file.id, file.can_restart === true);
      claimRef.current = next;
      setProcessMode(mode);
      setProcessTarget(null);
      setClaim(next);
      await load(true);
    });
  const formatDate = (value: string) =>
    new Date(value).toLocaleString(currentLanguage, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });

  return (
    <PageLayout>
      <Header
        title={t('Bandeja de entrada')}
        description={t(
          'Recibe liquidaciones por correo y procésalas desde aquí.'
        )}
        actions={
          <Button
            variant='flat'
            startContent={<Icon icon='tabler:refresh' />}
            isLoading={loading}
            onPress={() => void load()}
          >
            {t('Actualizar')}
          </Button>
        }
      />

      <div className='space-y-4'>
        {accessFailed && (
          <div role='status'>
            <Alert variant='warning' showIcon>
              {t(
                'No pudimos actualizar la bandeja. Tu revisión sigue abierta; reintentaremos automáticamente.'
              )}
            </Alert>
          </div>
        )}
        {addresses.length > 0 && (
          <Card shadow='sm'>
            <CardBody className='gap-3 p-4'>
              <span className='text-sm font-medium'>
                {t('Tu correo para liquidaciones')}
              </span>
              {addresses.map(({ address: value }) => (
                <div key={value} className='flex flex-wrap items-center gap-3'>
                  <span className='break-all text-lg font-semibold'>
                    {value}
                  </span>
                  <Button
                    size='sm'
                    variant='flat'
                    startContent={<Icon icon='tabler:copy' />}
                    onPress={() => void copy(value)}
                  >
                    {t('Copiar dirección')}
                  </Button>
                  <Button
                    size='sm'
                    variant='light'
                    onPress={() =>
                      void copy(
                        t(
                          'Por favor envía las liquidaciones como archivos PDF o Excel e incluye {{address}} en copia.',
                          { address: value }
                        )
                      )
                    }
                  >
                    {t('Copiar instrucciones')}
                  </Button>
                </div>
              ))}
              <p className='text-sm text-default-500'>
                {t(
                  'Reenvía tus archivos o pide a tus recibidores que incluyan esta dirección en copia. Se muestran todas las especies y temporadas.'
                )}
              </p>
            </CardBody>
          </Card>
        )}

        {isSuperadmin && (
          <Button
            as={Link}
            href='/superadmin-inbox-addresses'
            variant='flat'
            className='self-start'
          >
            {t('Administrar correos de liquidaciones')}
          </Button>
        )}

        <Tabs
          aria-label={t('Secciones de Inbox')}
          className='max-w-full'
          classNames={{ tabList: 'max-w-full overflow-x-auto' }}
          selectedKey={section}
          onSelectionChange={(key) => setSection(String(key))}
        >
          <Tab key='files' title={t('Archivos')} />
          <Tab
            key='senders'
            title={
              <span className='flex items-center gap-2'>
                {t('Remitentes')}
                {!!data?.pending_sender_count && (
                  <StatusChip tone='warning'>
                    {data.pending_sender_count} {t('por autorizar')}
                  </StatusChip>
                )}
              </span>
            }
          />
        </Tabs>
        {section === 'senders' ? (
          <InboxSenderApprovals key={exporterId} contextKey={exporterId} />
        ) : (
          <>
            <div className='flex flex-wrap justify-between items-center gap-3'>
              <Tabs
                aria-label={t('Estado de archivos')}
                className='max-w-full'
                classNames={{ tabList: 'max-w-full overflow-x-auto' }}
                selectedKey={filter}
                onSelectionChange={(key) => {
                  setFilter(String(key));
                  setPage(1);
                }}
              >
                <Tab key='pending' title={t('No procesados')} />
                <Tab key='all' title={t('Todos')} />
                <Tab key='completed' title={t('Procesados')} />
                <Tab key='archived' title={t('Archivados')} />
              </Tabs>
              {data?.sync.last_success_at && (
                <span className='text-xs text-default-400'>
                  {t('Última recepción')}:{' '}
                  {formatDate(data.sync.last_success_at)}
                </span>
              )}
            </div>
            {(error || data?.sync.last_error) && (
              <Alert variant={error ? 'danger' : 'warning'} showIcon>
                <span role='alert'>
                  {t(
                    error
                      ? 'No se pudo cargar la bandeja. Vuelve a intentar.'
                      : 'La recepción de algunos correos está demorada. Los archivos recibidos siguen disponibles.'
                  )}
                </span>
              </Alert>
            )}
            {loading && !data ? (
              <LoadingState label={t('Cargando archivos')} />
            ) : !data?.files.length ? (
              <EmptyState
                icon='tabler:mail-opened'
                title={t('No hay archivos en esta vista')}
                description={t(
                  'Los adjuntos recibidos aparecerán aquí. También puedes seguir subiendo archivos desde Detalle.'
                )}
                action={
                  <Button as={Link} href='/settlements/details' variant='flat'>
                    {t('Ir a Detalle')}
                  </Button>
                }
              />
            ) : (
              <div className='space-y-3'>
                {data.files.map((file) => {
                  const expired = Boolean(
                    file.lease_until &&
                      new Date(file.lease_until).getTime() < Date.now()
                  );
                  const canProcess =
                    !file.archived_at &&
                    !file.is_processed &&
                    (file.status === 'ready' ||
                      file.can_restart ||
                      (expired &&
                        ['reviewing', 'saving'].includes(file.status)));
                  return (
                    <Card key={file.id} shadow='sm'>
                      <CardBody className='p-4 flex flex-col md:flex-row gap-4 md:items-center'>
                        <div className='flex gap-3 min-w-0 flex-1'>
                          <div className='rounded-xl bg-default-100 p-3 self-start'>
                            {file.file_name.toLowerCase().endsWith('.pdf') ? (
                              <Icon icon='tabler:file-type-pdf' className='text-danger-500 text-xl' />
                            ) : /\.xlsx?$/i.test(file.file_name) ? (
                              <Icon icon='tabler:file-spreadsheet' className='text-success-600 text-xl' />
                            ) : (
                              <Icon icon='tabler:file' className='text-default-500 text-xl' />
                            )}
                          </div>
                          <div className='min-w-0 space-y-1'>
                            <p className='font-semibold break-words'>
                              {file.file_name}
                            </p>
                            <p className='text-sm text-default-500 break-words'>
                              {file.subject || t('Sin asunto')}
                            </p>
                            <p className='text-xs text-default-400 break-words'>
                              {file.sender} · {formatDate(file.received_at)} ·{' '}
                              {(file.file_size / 1024 / 1024).toFixed(2)} MB
                            </p>
                            {file.reason && (
                              <p className='text-sm text-warning-700'>
                                {t(
                                  reasons[file.reason] ||
                                    'No se pudo recibir este archivo.'
                                )}
                              </p>
                            )}
                            {file.status === 'duplicate' && (
                              <p className='text-sm text-default-500'>
                                {t(
                                  'Este archivo ya fue recibido. Revisa el documento original.'
                                )}
                              </p>
                            )}
                            {file.status === 'completed' &&
                              !file.is_processed && (
                                <p className='text-sm text-default-500'>
                                  {t(
                                    'Marcado para seguimiento. Las liquidaciones guardadas se conservan; esta marca no reinicia la importación.'
                                  )}
                                </p>
                              )}
                          </div>
                        </div>
                        <div className='flex flex-wrap items-center gap-2 md:justify-end'>
                          <Chip
                            size='sm'
                            variant='flat'
                            color={
                              file.status === 'quarantined'
                                ? 'warning'
                                : file.is_processed
                                  ? 'success'
                                  : file.status === 'rejected'
                                    ? 'warning'
                                    : 'default'
                            }
                          >
                            {t(
                              file.status === 'quarantined'
                                ? file.scan_status === 'infected'
                                  ? 'Bloqueado por seguridad'
                                  : file.scan_status === 'failed'
                                    ? 'No se pudo analizar'
                                    : file.scan_status === 'retry'
                                      ? 'Esperando reintento'
                                      : 'Verificando archivo'
                                : file.is_processed
                                  ? 'Procesado'
                                  : file.can_restart
                                    ? 'Revisión sin finalizar'
                                    : canProcess || file.status === 'completed'
                                      ? 'No procesado'
                                      : statuses[file.status]
                            )}
                          </Chip>
                          {!['rejected', 'quarantined'].includes(
                            file.status
                          ) && (
                            <Button
                              size='sm'
                              variant='light'
                              startContent={<Icon icon='tabler:eye' />}
                              isDisabled={Boolean(busy)}
                              onPress={() =>
                                void act(file.id, async () => {
                                  setPreview(
                                    await inboxRequest('preview', {
                                      id: file.id,
                                    })
                                  );
                                })
                              }
                            >
                              {t('Vista previa')}
                            </Button>
                          )}
                          {canProcess && (
                            <Button
                              size='sm'
                              color='primary'
                              isLoading={busy === file.id}
                              isDisabled={Boolean(busy) || Boolean(claim)}
                              onPress={() => setProcessTarget(file)}
                            >
                              {t(
                                file.can_restart
                                  ? 'Reabrir archivo'
                                  : 'Procesar archivo'
                              )}
                            </Button>
                          )}
                          {file.settlement_id && (
                            <Button
                              size='sm'
                              as={Link}
                              href={`/settlements/details/${file.settlement_id}`}
                              variant='flat'
                            >
                              {t('Ver liquidación')}
                            </Button>
                          )}
                          {file.can_retry_scan && (
                            <Button
                              size='sm'
                              variant='flat'
                              isDisabled={Boolean(busy)}
                              onPress={() =>
                                void act(file.id, async () => {
                                  await inboxRequest('retry-scan', {
                                    id: file.id,
                                  });
                                  await load(true);
                                })
                              }
                            >
                              {t('Reintentar análisis')}
                            </Button>
                          )}
                          {![
                            'receiving',
                            'quarantined',
                            'reviewing',
                            'saving',
                          ].includes(file.status) && (
                            <Button
                              size='sm'
                              variant='flat'
                              isDisabled={Boolean(busy) || Boolean(claim)}
                              onPress={() =>
                                void act(file.id, async () => {
                                  await inboxRequest('mark-processed', {
                                    id: file.id,
                                    processed: !file.is_processed,
                                  });
                                  await load(true);
                                })
                              }
                            >
                              {t(
                                file.is_processed
                                  ? 'Marcar como no procesado'
                                  : 'Marcar como procesado'
                              )}
                            </Button>
                          )}
                          {!['reviewing', 'saving'].includes(file.status) && (
                            <Button
                              size='sm'
                              variant='light'
                              aria-label={t(
                                file.archived_at ? 'Restaurar' : 'Archivar'
                              )}
                              startContent={<Icon icon='tabler:archive' />}
                              isDisabled={Boolean(busy)}
                              onPress={() =>
                                void act(file.id, async () => {
                                  await inboxRequest('archive', {
                                    id: file.id,
                                    archived: !file.archived_at,
                                  });
                                  await load(true);
                                })
                              }
                            >
                              {t(file.archived_at ? 'Restaurar' : 'Archivar')}
                            </Button>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  );
                })}
                {data.total > 25 && (
                  <Pagination
                    aria-label={t('Páginas')}
                    page={page}
                    total={Math.ceil(data.total / 25)}
                    onChange={setPage}
                    className='pt-3'
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
      <Modal
        isOpen={!!processTarget}
        onClose={() => {
          if (!busy) setProcessTarget(null);
        }}
        isDismissable={!busy}
        isKeyboardDismissDisabled={!!busy}
        hideCloseButton={!!busy}
      >
        <ModalContent>
          <ModalHeader>{t('¿Cómo quieres procesar este archivo?')}</ModalHeader>
          <ModalBody className='gap-4'>
            <p className='text-sm break-all'>{processTarget?.file_name}</p>
            {processTarget?.can_restart && (
              <p className='text-sm text-warning-700'>
                {t(
                  'Volverás a empezar desde el archivo original. Los cambios sin guardar no se recuperan y la revisión anterior quedará invalidada.'
                )}
              </p>
            )}
            <div className='rounded-xl border border-divider p-4 space-y-2'>
              <p className='font-semibold'>{t('Una liquidación')}</p>
              <p className='text-sm text-default-500'>
                {t(
                  'Usa el flujo habitual de carga y revisión de una liquidación, con este archivo ya seleccionado.'
                )}
              </p>
              <Button
                color='primary'
                isDisabled={!!busy}
                onPress={() => {
                  if (processTarget) void process(processTarget, 'single');
                }}
              >
                {t('Abrir carga habitual')}
              </Button>
            </div>
            <div className='rounded-xl border border-divider p-4 space-y-2'>
              <p className='font-semibold'>{t('PDF multi-embarque')}</p>
              <p className='text-sm text-default-500'>
                {t(
                  'Define grupos de páginas y distribuye el PDF entre varias referencias o embarques.'
                )}
              </p>
              <Button
                variant='flat'
                isDisabled={
                  !!busy ||
                  !processTarget?.file_name.toLowerCase().endsWith('.pdf')
                }
                onPress={() => {
                  if (processTarget) void process(processTarget, 'multi');
                }}
              >
                {t('Abrir multi-embarque')}
              </Button>
              {!processTarget?.file_name.toLowerCase().endsWith('.pdf') && (
                <p className='text-xs text-default-500'>
                  {t('Multi-embarque solo admite archivos PDF.')}
                </p>
              )}
            </div>
            {busy && <Spinner size='sm' label={t('Abriendo archivo')} />}
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              isDisabled={!!busy}
              onPress={() => setProcessTarget(null)}
            >
              {t('Cancelar')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {claim && processMode === 'single' && (
        <ExcelUploadModal
          key={claim.id}
          isOpen
          onClose={closeClaim}
          inboxClaim={claim}
          onSuccess={() => {
            claimRef.current = null;
            setClaim(null);
            setProcessMode(null);
            void load(true);
          }}
        />
      )}
      {claim && processMode === 'multi' && (
        <MultiShipmentUploadModal
          key={claim.id}
          isOpen
          onClose={closeClaim}
          inboxClaim={claim}
          onSuccess={() => {
            claimRef.current = null;
            setClaim(null);
            setProcessMode(null);
            void load(true);
          }}
        />
      )}
      <Modal
        isOpen={Boolean(preview)}
        onClose={() => setPreview(null)}
        size='5xl'
        scrollBehavior='inside'
        classNames={{ base: 'h-[90dvh] max-h-[90dvh]' }}
      >
        <ModalContent>
          <ModalBody className='p-4 flex-1 min-h-0 overflow-hidden'>
            {preview && (
              <>
                <div className='flex-1 min-h-0'>
                  <SettlementSourcePreview
                    remoteDocument={preview}
                    onClose={() => setPreview(null)}
                  />
                </div>
                {!!preview.settlementIds.length && (
                  <div className='flex flex-wrap gap-2 shrink-0 max-h-24 overflow-auto'>
                    {preview.settlementIds.map((id) => (
                      <Button
                        key={id}
                        as={Link}
                        href={`/settlements/details/${id}`}
                        variant='flat'
                      >
                        {t('Ver liquidación')} #{id}
                      </Button>
                    ))}
                  </div>
                )}
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </PageLayout>
  );
}
